// Client-side exports: PDF (jsPDF), ZIP (JSZip), CSV (no dep).
import jsPDF from "jspdf";
import JSZip from "jszip";
import { scoreLabel, scoreTier } from "@/lib/mockData";

function tierColor(tier) {
  if (tier === "genuine") return [34, 197, 94];
  if (tier === "inconclusive") return [245, 158, 11];
  return [239, 68, 68];
}

export function buildReportPdf(caseData) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 48;
  let y = margin;

  // Header band
  doc.setFillColor(10, 10, 11);
  doc.rect(0, 0, pageW, 80, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Yoobikwitus", margin, 48);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(160, 165, 175);
  doc.text("Forensic image verification report", margin, 64);

  y = 110;
  // Case meta
  doc.setTextColor(20, 20, 25);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Case ID:", margin, y);
  doc.setFont("helvetica", "normal");
  doc.text(caseData.id, margin + 60, y);

  doc.setFont("helvetica", "bold");
  doc.text("Submitted:", margin + 240, y);
  doc.setFont("helvetica", "normal");
  doc.text(new Date(caseData.submittedAt).toLocaleString(), margin + 310, y);

  y += 18;
  doc.setFont("helvetica", "bold");
  doc.text("File:", margin, y);
  doc.setFont("helvetica", "normal");
  doc.text(caseData.fileName || "—", margin + 60, y);

  doc.setFont("helvetica", "bold");
  doc.text("Source:", margin + 240, y);
  doc.setFont("helvetica", "normal");
  doc.text(caseData.sourceDomain || "—", margin + 310, y);

  // Score card
  y += 36;
  const tier = scoreTier(caseData.score);
  const [r, g, b] = tierColor(tier);
  doc.setDrawColor(r, g, b);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, pageW - margin * 2, 90, 8, 8, "FD");

  doc.setTextColor(r, g, b);
  doc.setFontSize(40);
  doc.setFont("helvetica", "bold");
  doc.text(`${caseData.score}`, margin + 24, y + 56);

  doc.setTextColor(40, 44, 52);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text("OVERALL CONFIDENCE SCORE", margin + 110, y + 32);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(scoreLabel(caseData.score), margin + 110, y + 56);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(120, 125, 135);
  doc.text(
    "Score range — Green > 70 (Likely genuine) • Amber 40–70 (Inconclusive) • Red < 40 (Likely AI-generated).",
    margin + 110,
    y + 74,
  );

  // Signals table
  y += 116;
  doc.setTextColor(20, 20, 25);
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text("Metadata signal breakdown", margin, y);
  y += 14;

  const colWidths = [140, 280, 70];
  doc.setFontSize(9);
  doc.setTextColor(110, 115, 125);
  doc.text("SIGNAL", margin, y);
  doc.text("FINDING", margin + colWidths[0], y);
  doc.text("SCORE", margin + colWidths[0] + colWidths[1], y);
  y += 8;
  doc.setDrawColor(220, 224, 230);
  doc.line(margin, y, pageW - margin, y);
  y += 12;

  doc.setFontSize(10);
  caseData.signals.forEach((s) => {
    const [sr, sg, sb] = tierColor(scoreTier(s.score));
    doc.setTextColor(40, 44, 52);
    doc.setFont("helvetica", "bold");
    doc.text(s.name, margin, y);
    doc.setFont("helvetica", "normal");
    const wrapped = doc.splitTextToSize(s.finding, colWidths[1] - 10);
    doc.text(wrapped, margin + colWidths[0], y);
    doc.setTextColor(sr, sg, sb);
    doc.setFont("helvetica", "bold");
    doc.text(`${s.score}`, margin + colWidths[0] + colWidths[1], y);
    y += Math.max(18, wrapped.length * 12 + 4);
    if (y > 760) {
      doc.addPage();
      y = margin;
    }
  });

  // Footer
  doc.setTextColor(140, 145, 155);
  doc.setFontSize(8);
  doc.text(
    `Generated by Yoobikwitus • yoobikwitus.com • ${new Date().toLocaleString()}`,
    margin,
    820,
  );

  return doc;
}

export function downloadReportPdf(caseData) {
  const doc = buildReportPdf(caseData);
  doc.save(`yoobikwitus-${caseData.id}.pdf`);
}

export async function downloadBatchZip(cases, batchName = "batch") {
  const zip = new JSZip();
  cases.forEach((c) => {
    const doc = buildReportPdf(c);
    const blob = doc.output("blob");
    zip.file(`yoobikwitus-${c.id}.pdf`, blob);
  });
  const content = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(content);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${batchName}-reports.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadCasesCsv(cases, filename = "yoobikwitus-cases.csv") {
  const header = [
    "case_id",
    "date_submitted",
    "file_name",
    "source_domain",
    "overall_score",
    "verdict",
    "flagged",
    "status",
  ];
  const rows = cases.map((c) => [
    c.id,
    new Date(c.submittedAt).toISOString(),
    c.fileName || "",
    c.sourceDomain || "",
    c.score,
    scoreLabel(c.score),
    c.flagged ? "true" : "false",
    c.status,
  ]);
  const csv =
    [header, ...rows]
      .map((r) =>
        r
          .map((v) => {
            const s = String(v ?? "");
            return s.includes(",") || s.includes('"') || s.includes("\n")
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(","),
      )
      .join("\n") + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
